<script setup>

import { ref, useTemplateRef, computed, inject, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { BASE_URL, parseResourcePath, sanitize } from '../utils.js';
import { followLink, go, toRoutePath } from '../router.js';
import {
  Breadcrumb,
  Button,
  ButtonGroup,
  Dialog,
  DirectoryView,
  FileUploader,
  PasswordInput,
  TextInput,
  useChangeBroadcast,
  useNotify
} from '@cloudron/pankow';
import DirectoryModel from '../models/DirectoryModel.js';
import FavoriteModel from '../models/FavoriteModel.js';
import ShareModel from '../models/ShareModel.js';
import PreviewPanel from './PreviewPanel.vue';
import EmptyState from './EmptyState.vue';
import RenameDialog from './RenameDialog.vue';
import { OPEN_WITH_VIEWERS, getOpenWithViewers } from '../viewers.js';
import { ROLES } from '../roles.js';

const { notify } = useNotify();

const DirectoryModelError = DirectoryModel.DirectoryModelError;

// item drags between cubby windows of the same site
const DRAG_SCOPE = 'cubby';

const emit = defineEmits(['invalid-session', 'share', 'filedrop', 'close-viewer', 'open-file', 'origin-back']);

const props = defineProps({
  clipboard: {
    type: Object,
    default: null,
  },
  originBack: {
    type: Boolean,
    default: false,
  },
});

const profile = inject('profile');
const route = useRoute();

function crumb(label, path, extra) {
  return Object.assign({
    label,
    route: `#${toRoutePath(path)}`,
    action: function (item, event) { followLink(event, path); }
  }, extra || {});
}

const beforeUnloadListener = (event) => {
  event.preventDefault();
  return window.confirm('File operation still in progress. Close anyway?');
};

const directoryBusy = ref(false);
const showSize = ref(true);
const activeResourceType = ref('');
const uiError = ref('');
const entry = ref({});
const entries = ref([]);
const selectedEntries = ref([]);
const currentResourcePath = ref('');
const currentShare = ref(null);
const currentGroup = ref(null);
const breadCrumbs = ref([]);
const breadCrumbHome = ref(crumb('My files', '/files/home/', { icon: 'fa-solid fa-house' }));
const viewMode = ref(localStorage.viewMode === 'grid' ? 'grid' : 'list');
const pendingViewer = ref(null);

function folderKey(resourcePath) {
  return sanitize(`${resourcePath}/`);
}

// other cubby windows of this browser reload changed folders
const changes = useChangeBroadcast('cubby-changes', DRAG_SCOPE);

changes.onChange(function (resourcePaths) {
  if (!currentResourcePath.value) return;
  if (resourcePaths.map(folderKey).includes(folderKey(currentResourcePath.value))) refresh();
});

const isGroupFoldersRoot = computed(() => activeResourceType.value === 'groupfolders' && breadCrumbs.value.length === 0);
const isSharesRoot = computed(() => activeResourceType.value === 'shares' && breadCrumbs.value.length === 0);
const showListingChrome = computed(() => {
  if (directoryBusy.value || entries.value.length) return true;
  return !isGroupFoldersRoot.value && !isSharesRoot.value;
});
const showDownload = computed(() => showListingChrome.value && entries.value.length > 0 && currentResourcePath.value !== '/groupfolders/');

const isReadonly = computed(() => {
  if (currentResourcePath.value === '/shares/') return true;
  if (currentResourcePath.value === '/groupfolders/') return true;
  if (currentShare.value) return currentShare.value.readonly;
  if (currentGroup.value) return currentGroup.value.myRole === ROLES.VIEWER;
  return false;
});

const showShare = computed(() => {
  if (activeResourceType.value === 'shares') return false;
  if (currentResourcePath.value === '/groupfolders/') return false;
  if (currentGroup.value && isReadonly.value) return false;
  return true;
});

function setViewMode(mode) {
  viewMode.value = mode;
  localStorage.viewMode = mode;
}

function onInvalidSession() {
  emit('invalid-session');
}

async function onToggleFavorite(favoriteEntry) {
  try {
    if (favoriteEntry.favorite) {
      await FavoriteModel.remove(favoriteEntry.favorite.id);
      favoriteEntry.favorite = null;
      favoriteEntry.star = false;
    } else {
      const payload = favoriteEntry.share
        ? { shareId: favoriteEntry.share.id, path: favoriteEntry.filePath }
        : { owner: favoriteEntry.owner, path: favoriteEntry.filePath };

      const id = await FavoriteModel.create(payload);
      favoriteEntry.favorite = { id, ...payload };
      favoriteEntry.star = true;
    }
  } catch (error) {
    if (error?.cause?.status === 401 || error?.status === 401) onInvalidSession();
    else console.error('Failed to toggle favorite', error);
  }
}

async function uploadJobPreFlightCheckHandler(job) {
  if (job.folder && await DirectoryModel.exists(parseResourcePath(job.targetFolder), job.folder)) {
    notify({ text: `Cannot upload. Folder ${job.folder} already exists.`, type: 'danger', timeout: 5000 });
    return false;
  }

  return true;
}

async function uploadHandler(targetDir, file, progressHandler) {
  const resource = parseResourcePath(targetDir);
  await DirectoryModel.upload(resource, file, progressHandler);
  await refresh();
  changes.announce([resource.resourcePath]);
}

const fileUploader = useTemplateRef('fileUploader');
const directoryView = useTemplateRef('directoryView');
const deleteDialog = useTemplateRef('deleteDialog');
const newItemDialogElement = useTemplateRef('newItemDialog');
const renameDialog = useTemplateRef('renameDialog');
const sharePasswordDialog = useTemplateRef('sharePasswordDialog');

const sharePasswordForm = ref({
  shareId: '',
  resourcePath: '',
  password: '',
  error: '',
  busy: false
});

function openSharePassword(shareId, resourcePath = null) {
  sharePasswordForm.value.shareId = shareId;
  sharePasswordForm.value.resourcePath = resourcePath || `/shares/${shareId}/`;
  sharePasswordForm.value.password = '';
  sharePasswordForm.value.error = '';
  sharePasswordForm.value.busy = false;
  sharePasswordDialog.value.open();
}

async function onSharePasswordSubmit() {
  if (sharePasswordForm.value.busy) return;

  const password = sharePasswordForm.value.password;
  if (!password) return;

  sharePasswordForm.value.busy = true;
  sharePasswordForm.value.error = '';

  try {
    await ShareModel.unlock(sharePasswordForm.value.shareId, password);
  } catch (error) {
    sharePasswordForm.value.busy = false;
    if (error?.cause?.status === 401) {
      sharePasswordForm.value.error = 'Invalid password.';
      return;
    }
    sharePasswordForm.value.error = 'Something went wrong. Please try again.';
    return;
  }

  sharePasswordForm.value.busy = false;
  sharePasswordDialog.value.close();

  // retry loading the current path
  await loadPath(sharePasswordForm.value.resourcePath, true);
}

const deletePending = ref([]);
const deleteBusy = ref(false);
const newItemForm = ref({
  mode: 'file',
  name: '',
  error: '',
  busy: false
});

function onNewItemDialogNameInput() {
  newItemForm.value.error = '';
}

function openNewFile() {
  newItemForm.value.busy = false;
  newItemForm.value.error = '';
  newItemForm.value.name = '';
  newItemForm.value.mode = 'file';
  newItemDialogElement.value.open();
}

function openNewFolder() {
  newItemForm.value.busy = false;
  newItemForm.value.error = '';
  newItemForm.value.name = '';
  newItemForm.value.mode = 'folder';
  newItemDialogElement.value.open();
}

function openUploadFile() {
  const resource = parseResourcePath(currentResourcePath.value || 'files/');
  fileUploader.value.onUploadFile(resource.resourcePath);
}

function openUploadFolder() {
  const resource = parseResourcePath(currentResourcePath.value || 'files/');
  fileUploader.value.onUploadFolder(resource.resourcePath);
}

async function onNewItemDialogSubmit() {
  if (newItemForm.value.busy) return;

  const name = newItemForm.value.name.trim();
  if (!name) return;

  newItemForm.value.busy = true;
  newItemForm.value.error = '';

  const resource = parseResourcePath(currentResourcePath.value || 'files/');
  const mode = newItemForm.value.mode;

  try {
    if (mode === 'file') await DirectoryModel.newFile(resource, name);
    else await DirectoryModel.newFolder(resource, name);
  } catch (error) {
    newItemForm.value.busy = false;
    if (error.reason === DirectoryModelError.NO_AUTH) {
      newItemDialogElement.value.close();
      onInvalidSession();
      return;
    }
    if (error.reason === DirectoryModelError.CONFLICT) {
      newItemForm.value.error = mode === 'file'
        ? 'A file with this name already exists.'
        : 'A folder with this name already exists.';
      return;
    }
    if (error.reason === DirectoryModelError.NOT_ALLOWED) {
      newItemForm.value.error = 'This name is not allowed.';
      return;
    }
    newItemForm.value.error = error.message || 'Something went wrong. Please try again.';
    console.error(mode === 'file' ? 'Failed to add file:' : 'Failed to add folder:', error);
    return;
  }

  newItemForm.value.busy = false;
  newItemDialogElement.value.close();
  await refresh();
  changes.announce([resource.resourcePath]);
  directoryView.value.highlightByName(name);
}

function clearSelection() {
  selectedEntries.value = [];
}

function reset() {
  entry.value = {};
  entries.value = [];
  clearSelection();
  breadCrumbs.value = [];
  breadCrumbHome.value = crumb('My files', '/files/home/', { icon: 'fa-solid fa-house' });
  activeResourceType.value = '';
  currentResourcePath.value = '';
  currentShare.value = null;
  currentGroup.value = null;
}

function onSelectionChanged(selectedItems) {
  selectedEntries.value = selectedItems;
}

async function onFileSaved(item, content, done) {
  try {
    await DirectoryModel.saveFile(item.resource, content);
  } catch (error) {
    console.error(`Failed to save file ${item.resourcePath}`, error);
  }

  if (typeof done === 'function') done();
}

async function downloadHandler(items) {
  if (items && !Array.isArray(items)) items = [ items ];
  if (!items) items = selectedEntries.value;
  if (items.length === 0) items = entries.value;
  if (items.length === 0) return;

  const resource = parseResourcePath(currentResourcePath.value);
  await DirectoryModel.download(resource, items);
}

function dragPayloadHandler(items) {
  return items.map(function (i) { return { name: i.name, extension: i.extension, resourcePath: i.resourcePath }; });
}

function filesFromDragPayload(payload) {
  if (!Array.isArray(payload)) return null;

  const files = payload
    .filter(function (f) { return typeof f?.name === 'string' && f.name && typeof f.extension === 'string' && typeof f.resourcePath === 'string' && f.resourcePath.startsWith('/'); })
    .map(function (f) { return { name: f.name, extension: f.extension, resourcePath: f.resourcePath, resource: parseResourcePath(f.resourcePath) }; });

  return files.length ? files : null;
}

async function pasteFiles(resource, action, files) {
  window.addEventListener('beforeunload', beforeUnloadListener, { capture: true });

  await DirectoryModel.paste(resource, action, files);
  await refresh();

  const sourceFolders = action === 'cut' ? files.map(function (f) { return f.resource.parentResourcePath; }).filter(Boolean) : [];
  changes.announce([resource.resourcePath, ...sourceFolders]);

  window.removeEventListener('beforeunload', beforeUnloadListener, { capture: true });
}

async function onDrop(targetFolder, dataTransfer, files, { payload = null, action = 'move' } = {}) {
  const fullTargetFolder = sanitize(`${currentResourcePath.value}/${targetFolder}`);

  if (payload) {
    files = filesFromDragPayload(payload);
    if (!files) return;
  }

  if (dataTransfer) {
    async function getFile(e) {
      return new Promise((resolve, reject) => {
        e.file(resolve, reject);
      });
    }

    // readEntries returns at most ~100 entries per call in Chromium; loop until empty
    async function readAllEntries(dirReader) {
      const entries = [];
      while (true) {
        const batch = await new Promise((resolve, reject) => { dirReader.readEntries(resolve, reject); });
        if (batch.length === 0) break;
        entries.push(...batch);
      }
      return entries;
    }

    const fileList = [];
    async function traverseFileTree(item, relativePath) {
      if (item.isFile) {
        const file = await getFile(item);
        // `webkitRelativePath` is read-only on File, so shadow it with an own property to
        // preserve the directory structure (DirectoryModel.upload keys off it)
        Object.defineProperty(file, 'webkitRelativePath', { value: relativePath, enumerable: false });
        fileList.push(file);
      } else if (item.isDirectory) {
        const dirItems = await readAllEntries(item.createReader());

        for (const dirItem of dirItems) {
          await traverseFileTree(dirItem, `${relativePath}/${dirItem.name}`);
        }
      } else {
        console.log('Skipping uknown file type', item);
      }
    }

    const droppedEntries = [];
    for (const item of dataTransfer.items) {
      const droppedEntry = item.webkitGetAsEntry();
      if (!droppedEntry) {
        console.warn('Dropped item not supported.', item, item.getAsString((s) => console.log(s)));
        continue;
      }
      droppedEntries.push(droppedEntry);
    }

    for (const droppedEntry of droppedEntries) {
      if (droppedEntry.isFile) {
        fileList.push(await getFile(droppedEntry));
      } else if (droppedEntry.isDirectory) {
        await traverseFileTree(droppedEntry, droppedEntry.name);
      }
    }
    fileUploader.value.addFiles(fileList, sanitize(`${currentResourcePath.value}/${targetFolder}`));
  } else {
    if (!files || !files.length) return;

    await pasteFiles(parseResourcePath(fullTargetFolder), action === 'copy' ? 'copy' : 'cut', files);
  }
}

async function deleteHandler(items) {
  if (!items) return;
  if (!Array.isArray(items)) items = [items];
  if (items.length === 0) return;

  deletePending.value = items;
  deleteBusy.value = false;
  deleteDialog.value.open();
}

function onDeleteCancel() {
  if (deleteBusy.value) return;
  deletePending.value = [];
}

async function onDeleteConfirm() {
  if (deleteBusy.value) return;

  const items = deletePending.value;
  if (items.length === 0) return;

  deleteBusy.value = true;
  window.addEventListener('beforeunload', beforeUnloadListener, { capture: true });

  for (const item of items) {
    try {
      const resource = parseResourcePath(sanitize(currentResourcePath.value + '/' + item.fileName));
      await DirectoryModel.remove(resource);
    } catch (e) {
      console.error(`Failed to remove file ${item.fileName}:`, e);
    }
  }

  await refresh();
  changes.announce([currentResourcePath.value]);

  window.removeEventListener('beforeunload', beforeUnloadListener, { capture: true });

  deleteDialog.value.close();
  deleteBusy.value = false;
}

async function extractHandler(item) {
  window.addEventListener('beforeunload', beforeUnloadListener, { capture: true });

  try {
    await DirectoryModel.extract(item.resource);
  } catch (error) {
    if (error.reason === DirectoryModelError.NO_AUTH) onInvalidSession();
    else if (error.reason === DirectoryModelError.PROCESSING_ERROR) {
      console.log(error);
      notify({ text: 'Failed to extract file: ' + error.message, persistent: true, type: 'danger' });
    } else {
      notify('Unknown error, check logs.');
    }

    window.removeEventListener('beforeunload', beforeUnloadListener, { capture: true });
    return;
  }
  await refresh();
  changes.announce([currentResourcePath.value]);

  window.removeEventListener('beforeunload', beforeUnloadListener, { capture: true });
}

async function pasteHandler(action, files, target) {
  if (!files || !files.length) return;

  const resource = parseResourcePath((target && target.isDirectory) ? sanitize(currentResourcePath.value + '/' + target.fileName) : currentResourcePath.value);
  await pasteFiles(resource, action, files);
}

function shareHandler(item) {
  emit('share', item);
}

function fileDropHandler(item) {
  emit('filedrop', item);
}

async function onRenameRequested(item) {
  renameDialog.value.open(item);
}

function onRenamed(newName) {
  refresh();
  changes.announce([currentResourcePath.value]);
  directoryView.value.highlightByName(newName);
}

async function onRefreshCurrentDirectory() {
  await refresh(null);
}

async function refresh(item = null) {
  if (item) {
    try {
      item = await DirectoryModel.get(item.resource, item.resource.path);
    } catch (error) {
      if (error.status === 401) return onInvalidSession();
      else if (error.status === 404) uiError.value = 'Does not exist';
      else console.error(error);
      return;
    }

    const idx = entries.value.findIndex((e) => e.id === item.id );
    if (idx !== -1) entries.value.splice(idx, 1, item);
  } else {
    directoryBusy.value = true;

    const resource = parseResourcePath(currentResourcePath.value);

    try {
      item = await DirectoryModel.get(resource, resource.path);
    } catch (error) {
      if (error.status === 401) return onInvalidSession();
      else if (error.status === 404) uiError.value = 'Does not exist';
      else console.error(error);
      return;
    }

    entry.value.files.forEach(function (e) {
      e.filePathNew = e.fileName;
    });

    entry.value = item;
    entries.value = item.files;
    directoryBusy.value = false;
  }
}

async function loadMainDirectory(path, item, forceLoad = false) {
  const resource = parseResourcePath(path);
  if (!resource) return;

  if (!forceLoad && currentResourcePath.value === resource.resourcePath) return;

  if (!item) {
    try {
      item = await DirectoryModel.get(resource, resource.path);
    } catch (error) {
      entries.value = [];
      item = {};

      if (error.status === 423) {
        if (resource.type === 'shares' && resource.shareId) openSharePassword(resource.shareId, resource.resourcePath);
        return;
      } else if (error.status === 401) return onInvalidSession();
      else if (error.status === 404) return uiError.value = 'Does not exist';
      else return console.error(error);
    }
  }

  activeResourceType.value = resource.type;
  currentResourcePath.value = resource.resourcePath;
  currentShare.value = item.share || null;
  currentGroup.value = item.group || null;

  if (resource.type === 'home') {
    breadCrumbs.value = sanitize(resource.path).split('/').filter(function (i) { return !!i; }).map(function (e, i, a) {
      return crumb(decodeURIComponent(e), '/files/home' + sanitize('/' + a.slice(0, i).join('/') + '/' + e));
    });
    breadCrumbHome.value = crumb('My files', '/files/home/', { icon: 'fa-solid fa-house' });
  } else if (resource.type === 'shares') {
    breadCrumbs.value = sanitize(resource.path).split('/').filter(function (i) { return !!i; }).map(function (e, i, a) {
      return crumb(decodeURIComponent(e), '/files/shares/' + resource.shareId + sanitize('/' + a.slice(0, i).join('/') + '/' + e));
    });
    breadCrumbHome.value = crumb('Shared with you', '/files/shares/', { icon: 'fa-solid fa-share-nodes' });

    if (item.share) {
      breadCrumbs.value.unshift(crumb(item.share.filePath.slice(1), '/files/shares/' + resource.shareId + '/'));
    }
  } else if (resource.type === 'groupfolders') {
    breadCrumbs.value = sanitize(resource.path).split('/').filter(function (i) { return !!i; }).map(function (e, i, a) {
      return crumb(decodeURIComponent(e), '/files/groupfolders/' + resource.groupId + sanitize('/' + a.slice(0, i).join('/') + '/' + e));
    });
    breadCrumbHome.value = crumb('Group folders', '/files/groupfolders/', { icon: 'fa-solid fa-user-group' });

    if (item.group) {
      breadCrumbs.value.unshift(crumb(item.group.name, '/files/groupfolders/' + item.group.id + '/'));
    }
  } else {
    console.error('FIXME breadcrumbs for resource type', resource.type);
  }

  item.files.forEach(function (e) {
    e.filePathNew = e.fileName;
  });

  entry.value = item;
  entries.value = item.files;
  emit('close-viewer');
}

async function reload() {
  directoryBusy.value = true;
  await loadMainDirectory(currentResourcePath.value, null, true);
  directoryBusy.value = false;
}

async function loadPath(path, forceLoad = false) {
  const resource = parseResourcePath(path || currentResourcePath.value);

  emit('close-viewer');

  if (!forceLoad && currentResourcePath.value === resource.resourcePath) return true;

  directoryBusy.value = true;

  let item;
  try {
    item = await DirectoryModel.get(resource);
  } catch (error) {
    entries.value = [];
    entry.value = {};

    if (error.status === 423) {
      if (resource.type === 'shares' && resource.shareId) openSharePassword(resource.shareId, resource.resourcePath);
      return false;
    } else if (error.status === 401 || error.status === 403) {
      onInvalidSession();
      return false;
    } else if (error.status === 404) {
      console.error('Failed to load entry', resource, error);
      notify({ text: `File or folder ${resource.path} does not exist`, type: 'danger', persistent: true });
      return false;
    } else {
      console.error(error);
      return false;
    }
  }

  if (item.isDirectory) await loadMainDirectory(resource.resourcePath, item, forceLoad);
  else await loadMainDirectory(resource.parentResourcePath, null, forceLoad);

  if (!item.isDirectory) {
    emit('open-file', { item, resource, siblingEntries: entries.value, viewer: pendingViewer.value });
    pendingViewer.value = null;
  } else {
    emit('close-viewer');
    clearSelection();
  }

  entry.value = item;
  directoryBusy.value = false;

  return true;
}

function onOpen(item) {
  if (item.share && item.share.id) go(`/files/shares/${item.share.id}${item.filePath}`);
  else if (item.group && item.group.id) go(`/files/groupfolders/${item.group.id}${item.filePath}`);
  else go(`/files/home${item.filePath}`);
}

function openWith(item, viewerId) {
  if (!item) return;
  pendingViewer.value = viewerId;
  onOpen(item);
}

function onUp() {
  if (props.originBack) {
    emit('origin-back');
    return;
  }

  const path = route.path.replace(/^\//, '');
  let next;
  if (path.indexOf('shares/') === 0) {
    const hash = path.slice('shares/'.length);
    if (!hash.split('/')[1]) next = '/shares/';
    else next = '/' + hash.split('/')[0] + sanitize(hash.split('/').filter(function (p) { return !!p; }).slice(1, -1).join('/'));
  } else {
    next = '/' + path.split('/')[0] + sanitize(path.split('/').filter(function (p) { return !!p; }).slice(1, -1).join('/'));
  }
  if (!next.endsWith('/')) next += '/';
  go(next);
}

onMounted(() => {
  showSize.value = window.innerWidth >= 576;

  const model = directoryView.value?.contextMenuModel;
  if (!model) return;

  const shareIdx = model.findIndex(i => i.id === 'share');
  if (shareIdx === -1) return;

  model.splice(shareIdx + 1, 0, {
    id: 'filedrop',
    label: 'File drop',
    icon: 'fa-solid fa-cloud-arrow-up',
    action: () => {
      fileDropHandler(directoryView.value?.focusItem);
    },
    disabled: () => {
      const item = directoryView.value?.focusItem;
      return !item || item.isFile;
    },
    visible: () => showShare.value,
  });

  const openIdx = model.findIndex(i => i.id === 'open');
  if (openIdx === -1) return;

  OPEN_WITH_VIEWERS.forEach((def, index) => {
    model.splice(openIdx + 1 + index, 0, {
      id: `open-with-${def.id}`,
      label: def.label,
      action: () => {
        openWith(directoryView.value?.focusItem, def.id);
      },
      visible: () => {
        const item = directoryView.value?.focusItem;
        return !!getOpenWithViewers(item).find((v) => v.id === def.id);
      },
    });
  });
});

defineExpose({
  loadPath,
  reset,
  reload,
  openNewFile,
  openNewFolder,
  openUploadFile,
  openUploadFolder,
  isReadonly,
  currentResourcePath,
  downloadHandler,
  onFileSaved,
});

</script>

<template>
  <div class="file-browser">
    <div class="file-browser-layout">
      <div class="main-container-content">
        <div class="breadcrumb-bar">
          <Button icon="fa-solid fa-chevron-left" :disabled="breadCrumbs.length === 0" @click="onUp" plain tool></Button>
          <Breadcrumb :home="breadCrumbHome" :items="breadCrumbs" />
          <div style="flex-grow: 1"></div>
          <Button v-if="isGroupFoldersRoot && entries.length" icon="fa-solid fa-gear" href="#/settings" plain @click="followLink($event, '/settings')">Manage group folders</Button>
          <div v-if="showListingChrome" class="listing-controls">
            <Button v-if="showDownload" icon="fa-solid fa-download" outline @click="downloadHandler()">Download</Button>
            <ButtonGroup>
              <Button icon="fa-solid fa-list" secondary :outline="viewMode !== 'list' ? true : null" tool @click="setViewMode('list')" />
              <Button icon="fa-solid fa-grip" secondary :outline="viewMode !== 'grid' ? true : null" tool @click="setViewMode('grid')" />
            </ButtonGroup>
          </div>
        </div>
        <div class="directory-pane">
          <DirectoryView
            ref="directoryView"
            :busy="directoryBusy"
            :view-mode="viewMode"
            :show-star="!!profile?.username"
            :show-owner="false"
            :show-extract="currentResourcePath !== '/groupfolders/'"
            :show-size="showSize"
            :show-modified="true"
            :show-delete="!isReadonly"
            :show-new-file="!isReadonly"
            :show-new-folder="!isReadonly"
            :show-upload-file="!isReadonly"
            :show-upload-folder="!isReadonly"
            :show-paste="!isReadonly"
            :show-rename="!isReadonly"
            :show-download="currentResourcePath !== '/groupfolders/'"
            :show-select-all="currentResourcePath !== '/groupfolders/'"
            :show-copy="currentResourcePath !== '/groupfolders/'"
            :show-cut="!isReadonly"
            :show-share="showShare"
            :share-indicator-property="'isSharedWith'"
            :file-drop-indicator-property="'isFileDrop'"
            :editable="!isReadonly"
            :multi-download="true"
            @selection-changed="onSelectionChanged"
            @item-activated="onOpen"
            @rename-requested="onRenameRequested"
            :refresh-handler="onRefreshCurrentDirectory"
            :delete-handler="deleteHandler"
            :share-handler="shareHandler"
            :star-handler="onToggleFavorite"
            :paste-handler="pasteHandler"
            :download-handler="downloadHandler"
            :extract-handler="extractHandler"
            :new-file-handler="openNewFile"
            :new-folder-handler="openNewFolder"
            :upload-file-handler="openUploadFile"
            :upload-folder-handler="openUploadFolder"
            :drop-handler="onDrop"
            :drag-scope="DRAG_SCOPE"
            :drag-payload-handler="dragPayloadHandler"
            :clipboard="clipboard"
            :items="entries"
            :fallback-icon="`${BASE_URL}mime-types/application-x-generic.svg`"
          >
            <template #empty>
              <EmptyState v-if="activeResourceType === 'home' || (activeResourceType === 'shares' && breadCrumbs.length) || (activeResourceType === 'groupfolders' && breadCrumbs.length)" icon="fa-regular fa-folder" title="No files" />
              <EmptyState
                v-else-if="isGroupFoldersRoot"
                icon="fa-solid fa-user-group"
                title="No group folders"
              >
                <Button icon="fa-solid fa-gear" href="#/settings" @click="followLink($event, '/settings')">Manage group folders</Button>
              </EmptyState>
              <EmptyState v-else-if="activeResourceType === 'shares' && !breadCrumbs.length" icon="fa-solid fa-share-nodes" title="Nothing shared with you" description="Files and folders others shared with you will show up here" />
            </template>
          </DirectoryView>
        </div>
      </div>
      <PreviewPanel
        v-if="showListingChrome"
        :parent-entry="entry"
        :selected-entries="selectedEntries"
        :show-download="currentResourcePath !== '/groupfolders/'"
        :show-delete="!isReadonly"
        :show-share="showShare"
        @download="downloadHandler"
        @delete="deleteHandler"
        @share="shareHandler"
      />
    </div>

    <FileUploader
      ref="fileUploader"
      :upload-handler="uploadHandler"
      :job-pre-flight-check-handler="uploadJobPreFlightCheckHandler"
      @finished="refresh"
    />

    <Dialog
      :title="newItemForm.mode === 'file' ? 'New filename' : 'New folder name'"
      ref="newItemDialog"
      reject-label="Cancel"
      reject-style="secondary"
      confirm-label="Save"
      :confirm-busy="newItemForm.busy"
      :confirm-active="!!newItemForm.name.trim()"
      @confirm="onNewItemDialogSubmit"
    >
      <form @submit.prevent="onNewItemDialogSubmit">
        <TextInput
          id="newItemNameInput"
          v-model="newItemForm.name"
          :placeholder="newItemForm.mode === 'file' ? 'Filename' : 'Folder name'"
          autofocus
          style="width: 100%"
          @update:model-value="onNewItemDialogNameInput"
        />
        <p class="has-error" v-show="newItemForm.error">{{ newItemForm.error }}</p>
      </form>
    </Dialog>

    <Dialog
      ref="deleteDialog"
      title="Confirm deletion"
      reject-label="Cancel"
      reject-style="secondary"
      confirm-label="Delete"
      confirm-style="danger"
      :confirm-busy="deleteBusy"
      @confirm="onDeleteConfirm"
      @close="onDeleteCancel"
    >
      <p v-if="deletePending.length === 1">
        Delete "{{ deletePending[0].fileName }}"?
      </p>
      <template v-else-if="deletePending.length > 1">
        <p>The following items will be deleted:</p>
        <ul class="delete-file-list">
          <li v-for="item in deletePending" :key="item.fileName">{{ item.fileName }}</li>
        </ul>
      </template>
    </Dialog>

    <RenameDialog ref="renameDialog" @rename="onRenamed" />

    <Dialog
      ref="sharePasswordDialog"
      title="Password required"
      reject-label="Cancel"
      reject-style="secondary"
      confirm-label="Unlock"
      :confirm-busy="sharePasswordForm.busy"
      :confirm-active="!!sharePasswordForm.password"
      @confirm="onSharePasswordSubmit"
    >
      <p>This share is protected. Enter the password to continue.</p>
      <form @submit.prevent="onSharePasswordSubmit">
        <PasswordInput
          id="sharePasswordInput"
          v-model="sharePasswordForm.password"
          placeholder="Password"
          autofocus
          style="width: 100%"
          @update:model-value="sharePasswordForm.error = ''"
        />
        <p class="has-error" v-show="sharePasswordForm.error">{{ sharePasswordForm.error }}</p>
      </form>
    </Dialog>
  </div>
</template>

<style scoped>

.file-browser {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-grow: 1;
  min-height: 0;
  height: 100%;
}

.file-browser-layout {
  display: flex;
  overflow: hidden;
  flex-grow: 1;
  min-height: 0;
}

.main-container-content {
  position: relative;
  overflow: hidden;
  flex-grow: 1;
}

.breadcrumb-bar {
  display: flex;
  gap: 4px;
  margin: auto 0px;
  padding: 4px;
  align-items: center;
}

.listing-controls {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-right: 40px;
}

.directory-pane {
  overflow: hidden;
  height: calc(100% - 46px);
}

.delete-file-list {
  margin: 8px 0 0;
  padding-left: 20px;
  max-height: 200px;
  overflow-y: auto;
}

</style>
